import { lazy, Suspense, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';

import { ScrollArea, LoadingSpinner } from '@app/shared/ui/atoms/Common';
import { Loading } from '@app/shared/ui/organisms/Common';
import { TopSection } from '@app/shared/ui/organisms/MatrixChannel';
import { NotFound } from '@app/shared/ui/molecules/Common';
import { useGetCurrentUserProfileQuery } from '@app/store/slices/users/usersApi';
import { useGetMatrixByStoneIdQuery } from '@app/store/slices/content/contentApi';
import { usePreloadImages } from '@app/shared/hooks/usePreloadImages';
import { MATRIX_CHANNEL_PRELOAD_IMAGES } from '@app/assets';

const Carousel = lazy(() => import('@app/shared/ui/organisms/MatrixChannel/Carousel'));

export default function MatrixChannelPage() {
  const navigate = useNavigate();
  const { id, index } = useParams();
  const matrixIndex = index ? Number(index) : undefined;
  const { data: user, isLoading: isUserLoading } = useGetCurrentUserProfileQuery();
  const {
    data: stoneMatrix,
    isLoading: isMatrixLoading,
    isError: isMatrixError,
  } = useGetMatrixByStoneIdQuery(id ?? skipToken);
  const isImagesLoading = usePreloadImages(MATRIX_CHANNEL_PRELOAD_IMAGES);

  const { current, prev, next } = useMemo(() => {
    if (!stoneMatrix || matrixIndex === undefined) {
      return { current: undefined, prev: undefined, next: undefined };
    }

    const currentIndex = stoneMatrix.findIndex((item) => item.cellIndex === matrixIndex);

    if (currentIndex === -1) {
      return { current: undefined, prev: undefined, next: undefined };
    }

    return {
      current: stoneMatrix[currentIndex],
      prev: stoneMatrix[currentIndex - 1],
      next: stoneMatrix[currentIndex + 1],
    };
  }, [stoneMatrix, matrixIndex]);

  const handleOtherArcanaBtnClick = (cellIndex: number) => {
    void navigate(`/matrix/channels/${id ?? '-'}/${String(cellIndex)}`);
  };

  if (isUserLoading || isMatrixLoading || isImagesLoading) {
    return <Loading type="magic" />;
  }

  if (isMatrixError) {
    return <NotFound />;
  }

  const stoneRewardButtonVisible = Boolean(
    id === '1' &&
    index === '0' &&
    user &&
    'firstStoneRewardClaimed' in user &&
    user.firstStoneRewardClaimed === false
  );

  return (
    <ScrollArea>
      {current && (
        <>
          <TopSection
            channelIdx={current.arcana}
            userAvatar={user?.avatar}
            userGender={user?.gender}
            title={current.cellTitle}
            description={current.arcanaDescription}
            tags={current.arcanaName}
          />
          <Suspense fallback={<LoadingSpinner size="lg" />}>
            <Carousel
              storyData={current.storyData}
              arcanaImage={current.icon}
              nextArcana={next}
              prevArcana={prev}
              onOtherArcanaBtnClick={handleOtherArcanaBtnClick}
              isRewardButtonVisible={stoneRewardButtonVisible}
            />
          </Suspense>
        </>
      )}
    </ScrollArea>
  );
}
